/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import axios from '../config/axiosConfig';
import { useThemeMode } from './ThemeContext';

const DEBUG = import.meta?.env?.MODE !== 'production';
const log = (...args) => DEBUG && console.log('%c[Auth]', 'color:#6b5cff', ...args);
const err = (...args) => DEBUG && console.error('%c[Auth]', 'color:#ff4d4f', ...args);

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

// ---- dev-only TTL cache for /token/validate/ --------------------------------
const TOKEN_VALIDATE_TTL = 2000; // 2 seconds
let tokenValidateCache = null;
let tokenValidatePromise = null;
let lastTokenValidateTime = 0;

// Dev-only helper to deduplicate /token/validate/ calls
async function validateTokenWithCache() {
  const now = Date.now();
  
  // In production, always make the request
  if (!DEBUG) {
    return axios.get('/accounts/api/v1/token/validate/', { withCredentials: true });
  }
  
  // In dev: check cache first
  if (tokenValidateCache && (now - lastTokenValidateTime) < TOKEN_VALIDATE_TTL) {
    log('validateTokenWithCache (cache hit)');
    return tokenValidateCache;
  }
  
  // In dev: deduplicate in-flight requests
  if (tokenValidatePromise) {
    log('validateTokenWithCache (await inflight)');
    return tokenValidatePromise;
  }
  
  // In dev: make new request and cache result
  tokenValidatePromise = (async () => {
    try {
      log('validateTokenWithCache (new request)');
      const result = await axios.get('/accounts/api/v1/token/validate/', { withCredentials: true });
      tokenValidateCache = result;
      lastTokenValidateTime = now;
      return result;
    } finally {
      tokenValidatePromise = null;
    }
  })();
  
  return tokenValidatePromise;
}

// ---- new: memo & de-dupe for role derivation --------------------------------
const roleCache = new Map();             // key: `${id}|${email}` -> {roles, staff_role}
let inflightKey = null;
let inflightPromise = null;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { setMode } = useThemeMode();

  // Align the active theme with the logged-in user's saved preference.
  const applyUserTheme = useCallback((apiUser) => {
    const mode = apiUser?.theme_mode;
    if (mode === 'light' || mode === 'dark') {
      setMode(mode);
    }
  }, [setMode]);

  // Persist the current user's theme choice to the backend (per-account, so a
  // shared computer reflects whoever is logged in) and update local state.
  const saveThemeMode = useCallback(async (mode) => {
    setMode(mode);
    setUser((prev) => (prev ? { ...prev, theme_mode: mode } : prev));
    try {
      await axios.patch('/accounts/api/v1/preferences/theme/', { theme_mode: mode }, { withCredentials: true });
    } catch (e) {
      err('saveThemeMode failed:', e?.response?.status, e?.response?.data || e.message);
    }
  }, [setMode]);

  // robust matcher: handles `user` being an object or a plain id
  const matchByUser = useCallback((rec, id, email) => {
    const u = rec?.user;
    return (
      u === id ||
      u?.id === id ||
      (email && (u?.email === email || rec?.email === email))
    );
  }, []);

  // tiny helper to try a sequence of param shapes without scanning the world
  const tryListOnce = useCallback(async (baseUrl, params) => {
    try {
      const res = await axios.get(baseUrl, { params, withCredentials: true });
      const data = res?.data;
      const list = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
      return list;
    } catch (e) {
      // swallow 400s from unsupported filters; just move to next strategy
      return [];
    }
  }, []);

  const findProfile = useCallback(async (baseUrl, id, email) => {
    // Prefer exact filters; fall back to search as last resort. Keep it to O(1) requests.
    const attempts = [];

    if (id != null) {
      attempts.push({ user: id, page_size: 1 });
      attempts.push({ user__id: id, page_size: 1 });
    }
    if (email) {
      attempts.push({ user__email: email, page_size: 1 });
      attempts.push({ search: email, page_size: 1 });
    }

    for (const params of attempts) {
      const list = await tryListOnce(baseUrl, params);
      if (!list.length) continue;

      const match = list.find(item => matchByUser(item, id, email));
      if (match) return match;
    }

    return null; // NO full scan
  }, [tryListOnce, matchByUser]);

  const deriveRoles = useCallback(async (apiUser) => {
    const id = apiUser?.id ?? null;
    const email = apiUser?.email ?? null;
    const cacheKey = `${id ?? ''}|${email ?? ''}`;

    if (roleCache.has(cacheKey)) {
      const cached = roleCache.get(cacheKey);
      log('deriveRoles (cache hit):', cached);
      return cached;
    }
    if (inflightKey === cacheKey && inflightPromise) {
      log('deriveRoles (await inflight)');
      return inflightPromise;
    }

    const runner = (async () => {
      const roles = new Set();

      // honor backend booleans if present
      if (apiUser?.is_teacher) roles.add('teacher');
      if (apiUser?.is_student) roles.add('student');
      if (apiUser?.is_staff) roles.add('staff');

      let staffRoleName = null;

      // targeted lookups only (no full scans)
      const staff = await findProfile('/accounts/api/v1/profile/staffs/', id, email);
      if (staff) {
        console.log('🔍 Debug - Staff profile found:', staff);
        roles.add('staff');
        const r = staff.role;
        console.log('🔍 Debug - Staff role object:', r);
        const name = typeof r === 'string' ? r : r?.name;
        console.log('🔍 Debug - Staff role name:', name);
        if (name) {
          staffRoleName = String(name).toLowerCase();
          roles.add(staffRoleName); // e.g. manager/secretary/accountant
          console.log('🔍 Debug - Added staff role name to roles:', staffRoleName);
        }
      }

      const teacher = await findProfile('/accounts/api/v1/profile/teachers/', id, email);
      if (teacher || apiUser?.is_teacher) roles.add('teacher');

      const student = await findProfile('/accounts/api/v1/profile/students/', id, email);
      if (student || apiUser?.is_student) roles.add('student');

      const out = { roles: Array.from(roles), staff_role: staffRoleName ? { name: staffRoleName } : null };
      roleCache.set(cacheKey, out);
      console.log('🔍 Debug - Final deriveRoles result:', out);
      log('deriveRoles result:', out);
      return out;
    })();

    inflightKey = cacheKey;
    inflightPromise = runner.finally(() => {
      inflightKey = null;
      inflightPromise = null;
    });
    return inflightPromise;
  }, [findProfile]);

  // StrictMode guard: ensure validateUser runs only once in dev
  const ranOnceRef = useRef(false);

  useEffect(() => {
    if (ranOnceRef.current) return;
    ranOnceRef.current = true;

    const validateUser = async () => {
      setIsLoading(true);
      try {
        log('Validating user…');
        const res = await validateTokenWithCache();
        log('/token/validate status:', res.status);

        if (res.status === 200 && res.data?.isValid && res.data?.user) {
          const apiUser = res.data.user;
          const { roles, staff_role } = await deriveRoles(apiUser);
          setUser({ ...apiUser, roles, staff_role });
          applyUserTheme(apiUser);
        } else {
          setUser(null);
          localStorage.removeItem('JWT_COOKIE_MUSIC_ONLINE');
        }
      } catch (error) {
        err('validate error:', error?.response?.status, error?.response?.data || error.message);
        setUser(null);
        localStorage.removeItem('JWT_COOKIE_MUSIC_ONLINE');
      } finally {
        setIsLoading(false);
      }
    };

    validateUser();
  }, [deriveRoles, applyUserTheme]);

  const login = async (apiUser) => {
    log('login() called with:', apiUser);
    try {
      const { roles, staff_role } = await deriveRoles(apiUser);
      setUser({ ...apiUser, roles, staff_role });
      applyUserTheme(apiUser);
    } catch (e) {
      err('login() role derivation failed, falling back to raw user:', e?.message || e);
      setUser(apiUser);
      applyUserTheme(apiUser);
    }
  };

  const logout = async () => {
    try {
      log('logout() → calling /logout/');
      await axios.post('/accounts/api/v1/logout/', {}, { withCredentials: true });
    } catch (e) {
      err('Error during logout:', e?.response?.status, e?.response?.data || e.message);
    } finally {
      setUser(null);
      localStorage.removeItem('JWT_COOKIE_MUSIC_ONLINE');
      log('logout() complete: user cleared and token removed');
    }
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout, saveThemeMode }}>
      {children}
    </AuthContext.Provider>
  );
};
